/**
 * <mega-menu-nav> — opens and closes the mega-menu panels.
 *
 * Desktop opens on hover with a short close delay, so crossing a gap between
 * the trigger and its panel doesn't snap it shut. Touch and keyboard use click,
 * which is why the trigger is a real <button>.
 *
 * Only one panel is open at a time.
 */
const MEGA_CLOSE_DELAY_MS = 180;

/**
 * Items already carrying listeners. The garage survives every menu swap, so
 * without this it would collect a second set of handlers each time and toggle
 * itself shut on the same click that opened it.
 */
const megaWired = new WeakSet();

class MegaMenuNav extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.items = [];
    // Matches the CSS breakpoint where panels start floating; below it they
    // open inline as an accordion, where hover-to-open would be wrong.
    this.hoverCapable = window.matchMedia('(hover: hover)').matches
      && window.matchMedia('(min-width: 48rem)').matches;

    this.refresh();

    // Escape closes and returns focus to the trigger that opened it.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = this.items.find((i) => i.hasAttribute('data-open'));
      if (!open) return;
      open.querySelector('[data-mega-trigger]')?.focus();
      this.close(open);
    });

    // Clicking away closes; focus leaving the nav does too, so tabbing out
    // doesn't strand an open panel behind the rest of the page.
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.closeAll();
    });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.closeAll();
    });
  }

  /**
   * (Re)scan for menu items and wire up any that are new.
   *
   * The item list can't be captured once: <vehicle-nav> swaps the whole menu
   * out for a machine-specific one long after this element has connected.
   */
  refresh() {
    this.items = [...this.querySelectorAll('[data-mega-item]')];

    for (const item of this.items) {
      const trigger = item.querySelector('[data-mega-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!trigger || !panel) continue;
      if (megaWired.has(item)) continue;
      megaWired.add(item);

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle(item);
      });

      if (this.hoverCapable) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          this.open(item);
        });
        item.addEventListener('mouseleave', () => {
          this.closeTimer = setTimeout(() => this.close(item), MEGA_CLOSE_DELAY_MS);
        });
      }
    }
  }

  toggle(item) {
    if (item.hasAttribute('data-open')) this.close(item);
    else this.open(item);
  }

  open(item) {
    for (const other of this.items) {
      if (other !== item) this.close(other);
    }
    item.setAttribute('data-open', '');
    item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'true');
    const panel = item.querySelector('[data-mega-panel]');
    if (panel) {
      panel.hidden = false;
      this.align(panel);
    }
  }

  /**
   * Keep an opened panel inside the viewport.
   *
   * Panels hang from the left edge of their own trigger, which runs a wide one
   * off the screen when that trigger sits near the right. No static rule can
   * anticipate it: the machine's panel is far wider than the trigger it hangs
   * off, and that trigger sits wherever the default menu beside it ends.
   * Measure the panel where it landed and shift it back.
   *
   * The bound is the nav row, not the viewport — it is the site's content
   * column, so a panel clamped to it lands flush with the rest of the page
   * instead of against the edge of the screen.
   */
  align(panel) {
    panel.style.transform = '';
    // Below the breakpoint panels open inline as an accordion, where there is
    // nothing to align.
    if (getComputedStyle(panel).position === 'static') return;

    const box = panel.getBoundingClientRect();
    const bounds = this.getBoundingClientRect();
    const wanted = Math.min(
      Math.max(bounds.left, box.left),
      bounds.right - box.width
    );

    // A transform, not `left`: the correction has to work for the garage panel
    // too, and that one is anchored by `right`. Nudging `left` on it would
    // over-constrain the box and quietly re-anchor it from the other side,
    // whereas a translate is relative to wherever the panel already landed.
    const shift = wanted - box.left;
    if (shift) panel.style.transform = `translateX(${shift}px)`;
  }

  close(item) {
    item.removeAttribute('data-open');
    item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
    const panel = item.querySelector('[data-mega-panel]');
    if (panel) panel.hidden = true;
  }

  closeAll() {
    clearTimeout(this.closeTimer);
    for (const item of this.items) this.close(item);
  }
}

if (!customElements.get('mega-menu-nav')) {
  customElements.define('mega-menu-nav', MegaMenuNav);
}

/**
 * Mobile menu disclosure. The nav row it toggles is the same markup desktop
 * uses, so there's no second copy of the menu to keep in step.
 */
(function () {
  'use strict';

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-menu-toggle]');
    if (!toggle) return;

    const row = document.querySelector('[data-nav-row]');
    if (!row) return;

    const open = row.toggleAttribute('data-open');
    toggle.setAttribute('aria-expanded', String(open));
  });
})();
